import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
    Tiles,
    Container,
    Content,
    Title,
    i18n,
    tokens,
    ChangeRequestModal,
    WaiveCoverageModal,
    ReinstateCoverageModal
} from '@simplybenefits/sb-ui-library';
import Settings from './Settings/Settings.js';
import ChangePassword from './ChangePassword.js';
import CreatePassword from './CreatePassword.js';
import Banking from './Banking/index.js';
import { initUpdateCreditCard, finalizeCreditCardUpdate, setCurrentSidebarItem, updateUserSettings } from '../../actions/user.js';
import * as notifier from '../../actions/notifier.js';
import { createChangeRequest, submitChangeRequest, deleteChangeRequest } from '../../actions/changeRequests.js';
import { createFolder, downloadFile, listFiles, removeFile, uploadFile } from '../../actions/files.js';

class Account extends Component {
    state = {
        settingsDialogOpen: false,
        changePasswordDialogOpen: false,
        createPasswordDialogOpen: false,
        bankingDialogOpen: false,
        changeRequestData: null,
        changeRequestModalOpen: false
    };

    componentDidMount() {
        this.props.dispatch(setCurrentSidebarItem('my-account'));

        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const sessionID = urlParams.get('session_id'); // stripe session_id
        const state = urlParams.get('state');

        if (sessionID) {
            this.props.dispatch(finalizeCreditCardUpdate(sessionID));
            window.location.href = '/my-account?state=success';
        }

        if (state === 'success') {
            this.props.dispatch(notifier.displayMessage(i18n.t(tokens.labels.account.creditCardUpdatedSuccessfully)));

            setTimeout(() => {
                window.location.href = '/my-account';
            }, 3000);
        }
    }

    _toggleDialog = (type) => {
        this.setState({
            [`${ type }DialogOpen`]: !this.state[`${ type }DialogOpen`]
        });
    };

    /**
     * Create and open a change request form
     * @param {Object} changeRequestData - Data for creating the change request
     * @returns {Promise<boolean>}
     */
    openChangeRequestModal = (changeRequestParams) => {
        this.setState({
            settingsDialogOpen: false,
            changeRequestModalOpen: true,
            changeRequestData: changeRequestParams
        });
    };

    /**
     * Close the change request modal
     */
    closeChangeRequestModal = () => {
        this.setState({
            changeRequestModalOpen: false,
            changeRequestData: null
        });
    };

    /**
     * 
     * @returns 
     */
    updateUserSettingsConditionally = async (changeRequestData, userUpdateData) => {
        if (changeRequestData && userUpdateData) {
            const userSettingsUpdateSuccess = await this.props.dispatch(updateUserSettings(userUpdateData, false));
            if (userSettingsUpdateSuccess) {
                const changeRequest = { type: "Edit Member Details", details: changeRequestData };
                this.openChangeRequestModal(changeRequest);
            }

            return true;
        }

        if (userUpdateData) {
            const userSettingsUpdateSuccess = await this.props.dispatch(updateUserSettings(userUpdateData));
            
            if (userSettingsUpdateSuccess) {
                window.location.reload();
                this._toggleDialog('settings');
                return true;
            }
        }

        if (changeRequestData) {
            const changeRequest = { type: "Edit Member Details", details: changeRequestData };
            this.openChangeRequestModal(changeRequest);
            return true;
        }

        return false;
    };

    _saveChangeRequest = async (...params) => {
        const success = await this.props.dispatch(submitChangeRequest(...params));
        
        if (success) {
            window.location.reload();
        }
    };

    render() {
        const { meta } = this.props;

        const isPasswordSet = meta.isPasswordSet !== false;

        const passwordTile = isPasswordSet
            ? {
                title: i18n.t(tokens.titles.changePassword),
                description: i18n.t(tokens.labels.account.changePasswordDescription),
                onClick: () => this._toggleDialog('changePassword'),
                testid: 'change-password-tile'
            }
            : {
                title: i18n.t(tokens.titles.createPassword),
                description: i18n.t(tokens.labels.account.createPasswordDescription),
                onClick: () => this._toggleDialog('createPassword'),
                testid: 'change-password-tile'
            };

        let tiles = [
            {
                title: i18n.t(tokens.titles.accountDetails),
                description: i18n.t(tokens.labels.account.accountSettingsDescription),
                onClick: () => this._toggleDialog('settings'),
                testid: 'settings-tile'
            },
            passwordTile,
            {
                title: i18n.t(tokens.titles.bankingDetails),
                description: i18n.t(tokens.labels.account.managePaymentReimbursementDetails),
                onClick: () => this._toggleDialog('banking'),
                testid: 'banking-tile'
            },
            {
                title: i18n.t(tokens.titles.dependents),
                description: i18n.t(tokens.labels.account.manageDependents),
                link: '/my-account/dependents',
                testid: 'dependents-tile'
            }
        ];

        if (!meta.isMarketplaceOnly) {
            if (meta.requiresBeneficiaries === true) {
                tiles.push({
                    title: i18n.t(tokens.titles.beneficiaries),
                    description: i18n.t(tokens.labels.account.manageBeneficiaries),
                    link: '/my-account/beneficiaries',
                    testid: 'beneficiaries-tile'
                });

                tiles.push({
                    title: i18n.t(tokens.titles.trustee),
                    description: i18n.t(tokens.labels.account.manageTrustee),
                    link: '/my-account/trustee',
                    testid: 'trustee-tile'
                });
            }

            // hide the tile for the SOGS group
            meta.groupNum !== "62250" &&
            tiles.push({
                title: i18n.t(tokens.labels.changeRequests.waiveCoverage),
                description: i18n.t(tokens.labels.changeRequests.requestToWaiveYourBenefits),
                onClick: () => this._toggleDialog('waiveCoverage'),
                testid: 'trustee-tile'
            });

            // hide the tile for the SOGS group
            (this.props.meta.hasWaived || this.props.meta.waiveFor) && meta.groupNum !== "62250" &&
            tiles.push({
                title: i18n.t(tokens.labels.changeRequests.reinstateCoverage),
                description: i18n.t(tokens.labels.changeRequests.requestToReinstateYourBenefits),
                onClick: () => this._toggleDialog('reinstateCoverage'),
                testid: 'trustee-tile'
            });

            tiles.push({
                title: i18n.t(tokens.labels.amendments.requestedChanges),
                description: i18n.t(tokens.labels.changeRequests.viewTheStatusOfYourRequestedChanges),
                link: '/my-account/requestedChanges',
                testid: 'trustee-tile'
            });
        }

        if (meta.divisionPaymentType === 'member' && meta.paymentType === 'CC') {
            tiles.splice(3, 0, {
                title: i18n.t(tokens.titles["update credit card"]),
                description: i18n.t(tokens.labels.account.updateCreditCardOnFile),
                onClick: async () => {
                    window.location.href = await this.props.dispatch(initUpdateCreditCard());
                },
                testid: 'banking-tile',
                icon: 'Banking Details'
            });
        }

        if (meta.isDependent) {
            const tile = isPasswordSet
                ? {
                    title: i18n.t(tokens.titles.changePassword),
                    description: i18n.t(tokens.labels.account.changePasswordDescription),
                    onClick: () => this._toggleDialog('changePassword'),
                    testid: 'change-password-tile'
                }
                : {
                    title: i18n.t(tokens.titles.createPassword),
                    description: i18n.t(tokens.labels.account.createPasswordDescription),
                    onClick: () => this._toggleDialog('createPassword'),
                    testid: 'change-password-tile'
                };

            tiles = [ tile ];
        }

        return (
            <Container>
                <Content>
                    <Title title={ i18n.t(tokens.titles["my account"]) } />
                    <Tiles tiles={tiles} />
                </Content>

                {
                    this.state.settingsDialogOpen &&
                        <Settings 
                            close={() => this._toggleDialog('settings')}
                            updateUserSettingsConditionally={ this.updateUserSettingsConditionally }
                        />
                }

                {
                    this.state.changePasswordDialogOpen &&
                        <ChangePassword close={() => this._toggleDialog('changePassword')}/>
                }

                {
                    this.state.createPasswordDialogOpen &&
                        <CreatePassword close={() => this._toggleDialog('createPassword')}/>
                }

                {
                    this.state.bankingDialogOpen &&
                        <Banking close={() => this._toggleDialog('banking')} />
                }

                <WaiveCoverageModal
                    open={this.state.waiveCoverageDialogOpen}
                    close={() => this._toggleDialog('waiveCoverage')}
                    loader={this.props.loader}
                    openChangeRequestModal={ (...params) => {
                        this._toggleDialog('waiveCoverage');
                        this.openChangeRequestModal(...params); 
                    }}
                />

                <ReinstateCoverageModal
                    open={this.state.reinstateCoverageDialogOpen}
                    close={() => this._toggleDialog('reinstateCoverage')}
                    loader={this.props.loader}
                    openChangeRequestModal={ (...params) => {
                        this._toggleDialog('reinstateCoverage');
                        this.openChangeRequestModal(...params); 
                    }}
                    userData={this.props.meta}
                />

                <ChangeRequestModal
                    open={ this.state.changeRequestModalOpen }
                    onClose={ this.closeChangeRequestModal }
                    createChangeRequest={(...params) => this.props.dispatch(createChangeRequest(...params))}
                    deleteChangeRequest={(...params) => this.props.dispatch(deleteChangeRequest(...params))}
                    submitChangeRequest={this._saveChangeRequest}
                    changeRequestData={this.state.changeRequestData}
                    loader={this.props.loader}

                    // Files Props
                    type="users"
                    id={ meta.userID }
                    dispatch={ this.props.dispatch }
                    listFiles={ listFiles }
                    createFolder={ createFolder }
                    uploadFile={ uploadFile }
                    downloadFile={ downloadFile }
                    removeFile={ removeFile }
                />
                <span id="my-account" />
            </Container>
        );
    }
}

Account.propTypes = {
    meta: PropTypes.object
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    meta: state.user.meta,
    loader: state.loader
});

export default (connect(mapStateToProps)(Account));